import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import PageHero, { SectionHeading } from "../components/PageHero";

const SchoolLife = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    // Wait for layout, then scroll to target
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const t = setTimeout(scroll, 60);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <PageLayout>
      <PageHero
        eyebrow="School Life"
        headline={<>A Rhythm of Prayer, Study, Beauty, and <em className="not-italic text-[#c9a961]">Work</em>.</>}
      />

      {/* Leisure — Josef Pieper */}
      <section className="bg-[#fbf7ee]">
        <div className="max-w-[880px] mx-auto px-6 md:px-10 lg:px-14 pt-10 md:pt-14 pb-2">
          <p className="font-serif-body text-[17px] md:text-[18px] leading-[1.9] text-[#3a466e]">
            Drawing from Josef Pieper's work <em>Leisure: The Basis of Culture</em>, we recognize that education requires more than continual work and academic striving. True leisure is not mere idleness, but a posture of receptivity — a willingness to pause, wonder, worship, create, celebrate, and delight in things that are good for their own sake. Our program alternates days of academic focus with days dedicated to formation, creativity, and movement. In this space beyond the ordinary academic schedule, students are given time not simply to accomplish more, but to receive, make, and delight in the goodness of God's world.
          </p>
        </div>
      </section>

      {/* Prayer Throughout the Day */}
      <section className="bg-[#fbf7ee]">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 lg:px-14 pt-10 md:pt-12 pb-20 md:pb-24">
          <SectionHeading eyebrow="Prayer Throughout the Day" title="The School Day Shaped by Prayer." />
          <div className="space-y-5 font-serif-body text-[17px] leading-[1.85] text-[#3a466e] max-w-[860px] mx-auto">
            <p>
              On <span className="font-display italic text-[#1e2952]">Mondays, Wednesdays, and Fridays</span>, the school day is punctuated by the traditional hours of the Little Office of the Blessed Virgin Mary. The community begins with <span className="font-display italic text-[#1e2952]">Prime</span>, pauses at midday for <span className="font-display italic text-[#1e2952]">Sext</span>, and returns to prayer in the afternoon for <span className="font-display italic text-[#1e2952]">None</span>.
            </p>
            <p>
              On <span className="font-display italic text-[#1e2952]">Tuesdays and Thursdays</span>, students attend the Holy Sacrifice of the Mass and pray the <span className="font-display italic text-[#1e2952]">Rosary</span> together.
            </p>
            <p>
              Through the prayers of the Little Office, the Rosary, and the Mass, students enter the Church's tradition of sacred prayer while learning to order the hours of ordinary life toward God.
            </p>

            <div className="not-italic mt-16 md:mt-20 flex justify-center">
              <Link
                to="/about"
                data-testid="school-life-link-mission"
                className="inline-block font-sans-nav text-[12px] tracking-[0.24em] uppercase px-8 py-4 border border-[#1e2952] text-[#1e2952] hover:bg-[#1e2952] hover:text-[#fbf7ee] transition-colors"
              >
                Our Mission
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1e2952] text-[#fbf7ee]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 lg:px-14 py-20 text-center">
          <h2 className="font-display text-[28px] md:text-[38px] leading-tight" style={{ fontWeight: 500 }}>
            A Day Ordered Toward the Good.
          </h2>
          <div className="mt-6 flex items-center justify-center gap-3">
            <span className="block h-px w-40 bg-[#c9a961]"></span>
            <span className="text-[#c9a961]">†</span>
            <span className="block h-px w-40 bg-[#c9a961]"></span>
          </div>
          <div className="mt-10 flex justify-center">
            <Link to="/inquire" className="inline-block font-sans-nav text-[12px] tracking-[0.28em] uppercase px-10 py-4 bg-[#c9a961] text-[#1e2952] hover:bg-[#e2c98a] transition-colors">
              Inquire Today
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default SchoolLife;
